import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  Brain, TrendingUp, TrendingDown, Minus, Shield, Zap, Heart,
  AlertTriangle, ChevronRight, Loader2, Activity, CheckCircle,
  Clock, Eye, RefreshCw, ArrowRight
} from 'lucide-react';

const STATE_THEME = {
  calm_alignment:        { bg: 'from-emerald-950/40 to-emerald-900/10', accent: '#34d399', text: 'text-emerald-300', icon: CheckCircle },
  performative_flow:     { bg: 'from-violet-950/40 to-violet-900/10', accent: '#a78bfa', text: 'text-violet-300', icon: Zap },
  nervous_system_strain: { bg: 'from-rose-950/40 to-rose-900/10', accent: '#f87171', text: 'text-rose-300', icon: AlertTriangle },
  cognitive_overload:    { bg: 'from-amber-950/40 to-amber-900/10', accent: '#fbbf24', text: 'text-amber-300', icon: Brain },
  anxiety_loop:          { bg: 'from-rose-950/40 to-pink-900/10', accent: '#fb7185', text: 'text-pink-300', icon: RefreshCw },
  emotional_fatigue:     { bg: 'from-purple-950/40 to-purple-900/10', accent: '#c084fc', text: 'text-purple-300', icon: Heart },
  recovery_debt:         { bg: 'from-slate-900/40 to-slate-800/10', accent: '#94a3b8', text: 'text-slate-300', icon: Clock },
  fragmented_focus:      { bg: 'from-orange-950/40 to-orange-900/10', accent: '#fb923c', text: 'text-orange-300', icon: Activity },
  irritable_reactivity:  { bg: 'from-red-950/40 to-red-900/10', accent: '#ef4444', text: 'text-red-300', icon: Zap },
};

const SCORE_META = [
  { key: 'regulation_score', label: 'Regulation', good: 'high' },
  { key: 'emotional_load_score', label: 'Emotional Load', good: 'low' },
  { key: 'reactivity_score', label: 'Reactivity', good: 'low' },
  { key: 'cognitive_strain_score', label: 'Cognitive Strain', good: 'low' },
  { key: 'resilience_score', label: 'Resilience', good: 'high' },
  { key: 'alignment_score', label: 'Alignment', good: 'high' },
];

const NODE_COLORS = {
  calm_alignment: '#34d399', performative_flow: '#a78bfa', nervous_system_strain: '#f87171',
  cognitive_overload: '#fbbf24', anxiety_loop: '#fb7185', emotional_fatigue: '#c084fc',
  recovery_debt: '#94a3b8', fragmented_focus: '#fb923c', irritable_reactivity: '#ef4444',
};

export default function TwinFloDashboard() {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [signature, setSignature] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('therapeutic');
  const [expandedDriver, setExpandedDriver] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/twinflo/today`).catch(() => ({ data: null })),
      axios.get(`${API}/twinflo/my-predictions`).catch(() => ({ data: { predictions: [] } })),
      axios.get(`${API}/twinflo/t3/my-recommendations`).catch(() => ({ data: { recommendations: [] } })),
    ]).then(([todayRes, predRes, recRes]) => {
      setToday(todayRes.data);
      setPredictions(predRes.data?.predictions || []);
      setStaffData(recRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#c8b89a' }} />
    </div>
  );

  if (!today) return (
    <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center px-6">
      <div className="text-center">
        <Brain className="w-10 h-10 mx-auto mb-4" style={{ color: '#c8b89a20' }} />
        <p className="text-sm" style={{ color: '#c8b89a50' }}>Your TwinFlo intelligence is being prepared...</p>
        <p className="text-xs mt-1" style={{ color: '#c8b89a30' }}>Complete a MindFlo check-in to activate</p>
      </div>
    </div>
  );

  const stateKey = today.current_state?.toLowerCase().replace(/\s+/g, '_') || 'calm_alignment';
  const theme = STATE_THEME[stateKey] || STATE_THEME.calm_alignment;
  const StateIcon = theme.icon;

  return (
    <div className="min-h-screen bg-[#0a0e14]" data-testid="twinflo-dashboard">
      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">

        {/* ═══ SECTION 1: CURRENT STATE (Hero) ═══ */}
        <div className={`rounded-2xl bg-gradient-to-br ${theme.bg} border border-white/[0.04] p-6 relative overflow-hidden`} data-testid="hero-state">
          {today.stability === 'unstable' && <div className="absolute inset-0 animate-pulse bg-white/[0.01] rounded-2xl" />}
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <StateIcon className="w-5 h-5" style={{ color: theme.accent }} />
              <span className="text-[10px] uppercase tracking-[0.2em] font-['Work_Sans']" style={{ color: `${theme.accent}90` }}>Your Current State</span>
            </div>
            <h1 className={`text-2xl sm:text-3xl font-['Outfit'] font-bold ${theme.text} mb-2`}>{today.current_state}</h1>
            <p className="text-sm leading-relaxed" style={{ color: '#c8b89a90' }}>{today.state_explanation}</p>

            {/* Status Strip */}
            <div className="flex items-center gap-3 mt-4">
              {today.stability && (
                <Badge className={`text-[9px] border-0 ${today.stability === 'stable' ? 'bg-emerald-500/10 text-emerald-400' : today.stability === 'unstable' ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {today.stability === 'stable' ? <Minus className="w-3 h-3 mr-0.5" /> : today.stability === 'unstable' ? <Activity className="w-3 h-3 mr-0.5" /> : <Activity className="w-3 h-3 mr-0.5" />}
                  {today.stability}
                </Badge>
              )}
              <Badge className={`text-[9px] border-0 ${today.confidence_level === 'high' ? 'bg-emerald-500/10 text-emerald-400' : today.confidence_level === 'low' ? 'bg-zinc-500/10 text-zinc-400' : 'bg-amber-500/10 text-amber-400'}`}>
                {today.confidence_level} confidence
              </Badge>
            </div>
            <p className="text-[10px] mt-2" style={{ color: '#c8b89a40' }}>{today.signal_message}</p>
          </div>
        </div>

        {/* ═══ SECTION 2: DRIVERS ═══ */}
        {today.why_this_is_happening?.length > 0 && (
          <div data-testid="drivers-section">
            <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans'] mb-3" style={{ color: '#c8b89a50' }}>What's Driving This</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {today.why_this_is_happening.map((driver, i) => (
                <button key={i} onClick={() => setExpandedDriver(expandedDriver === i ? null : i)}
                  className="text-left rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 hover:bg-white/[0.04] transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-['Outfit'] font-medium" style={{ color: '#c8b89a' }}>{driver}</span>
                    <ChevronRight className={`w-3 h-3 transition-transform ${expandedDriver === i ? 'rotate-90' : ''}`} style={{ color: '#c8b89a30' }} />
                  </div>
                  {expandedDriver === i && (
                    <p className="text-[10px] mt-2 leading-relaxed" style={{ color: '#c8b89a50' }}>
                      This factor is contributing to your current {today.current_state?.toLowerCase()} state. Addressing it may help shift your system toward balance.
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ═══ SECTION 3: PREDICTIONS ═══ */}
        {predictions.length > 0 && (
          <div data-testid="predictions-section">
            <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans'] mb-3" style={{ color: '#c8b89a50' }}>What May Happen Next</h2>
            <div className="space-y-2">
              {predictions.map((p, i) => (
                <div key={i} className={`rounded-xl border p-3.5 ${p.is_positive ? 'bg-emerald-950/20 border-emerald-500/10' : 'bg-amber-950/20 border-amber-500/10'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {p.is_positive ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                      <span className={`text-xs font-['Outfit'] font-medium ${p.is_positive ? 'text-emerald-300' : 'text-amber-300'}`}>{p.what_may_happen}</span>
                    </div>
                    <Badge className={`text-[8px] border-0 ${p.is_positive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {p.likelihood}
                    </Badge>
                  </div>
                  {p.watch_for?.length > 0 && (
                    <p className="text-[10px] ml-5.5" style={{ color: '#c8b89a40' }}>Watch for: {p.watch_for.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ SECTION 4: TIMELINE PREVIEW ═══ */}
        <div data-testid="timeline-preview">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans']" style={{ color: '#c8b89a50' }}>Your System Journey</h2>
          </div>
          <MiniTimeline />
        </div>

        {/* ═══ SECTION 5: SIGNATURE INSIGHT ═══ */}
        <SignatureInsight />

        {/* ═══ SECTION 6: SYSTEM SCORES ═══ */}
        <ScoresBars />

        {/* ═══ SECTION 7: WHAT TO DO TODAY (T3-Powered) ═══ */}
        {(() => {
          const t3Recs = staffData?.recommendations || [];
          const hasPersonal = t3Recs.some(r => r.personalized);
          const actions = hasPersonal ? t3Recs : (today.best_action_today || []).map(a => ({ action_label: a }));

          return actions.length > 0 ? (
            <div className="rounded-2xl border p-5" style={{ borderColor: `${theme.accent}20`, backgroundColor: `${theme.accent}05` }} data-testid="actions-section">
              <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans'] mb-1" style={{ color: `${theme.accent}80` }}>What To Do Today</h2>
              <p className="text-xs mb-3" style={{ color: '#c8b89a60' }}>
                {hasPersonal ? 'Personalized for you based on your response history' : 'Recommended based on your current state'}
              </p>
              <div className="space-y-2.5">
                {actions.slice(0, 3).map((action, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0" style={{ backgroundColor: `${theme.accent}15` }}>
                      <span className="text-[10px] font-bold" style={{ color: theme.accent }}>{i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm leading-relaxed" style={{ color: '#c8b89a' }}>
                        {action.action_label || action}
                      </p>
                      {action.personal_note && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px]" style={{ color: `${theme.accent}60` }}>{action.personal_note}</span>
                          {action.speed_note && <span className="text-[9px]" style={{ color: '#c8b89a30' }}>{action.speed_note}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* ═══ SECTION 8: MODE TOGGLE ═══ */}
        <div className="space-y-3">
          {/* Explore Your Future CTA */}
          <button onClick={() => navigate('/twinflo/simulate')} className="w-full rounded-2xl p-4 border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-colors text-left" data-testid="explore-future-cta">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans']" style={{ color: '#c8b89a40' }}>Simulation</div>
                <div className="text-sm font-['Outfit'] font-medium" style={{ color: '#c8b89a' }}>Explore Your Future</div>
                <div className="text-[10px] mt-0.5" style={{ color: '#c8b89a30' }}>See how your choices may shape your path</div>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: '#c8b89a20' }} />
            </div>
          </button>

          {/* Mode Toggle */}
          <div className="flex items-center justify-center gap-1 py-2" data-testid="mode-toggle">
          {['therapeutic', 'performative'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-xl text-[10px] uppercase tracking-[0.1em] font-['Work_Sans'] transition-all ${mode === m ? 'bg-white/[0.06] text-white/70 border border-white/[0.08]' : 'text-white/20 hover:text-white/40'}`}
              data-testid={`mode-${m}`}>
              {m}
            </button>
          ))}
          </div>
        </div>

        <div className="h-8" /> {/* Bottom spacing */}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MINI TIMELINE
// ═══════════════════════════════════════════════════════════

function MiniTimeline() {
  const [tl, setTl] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // The subscriber doesn't have access to /twinflo/timeline/{id} — use a workaround
    // We'll try to get it; if it fails, show placeholder
    axios.get(`${API}/twinflo/today`).then(r => {
      // We don't have timeline data from subscriber endpoint
      // So we'll show a simplified view based on available data
    }).catch(() => {});
  }, []);

  // Since subscriber can't access timeline endpoint directly, show a simplified visual
  // The full evolution is accessible via staff view
  return (
    <Card className="bg-white/[0.015] border-white/[0.04]">
      <CardContent className="py-4 px-4">
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center gap-1">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className="w-6 h-6 rounded-full" style={{
                  backgroundColor: i < 5 ? '#c084fc20' : i === 5 ? '#fbbf2420' : '#34d39920',
                  border: i === 6 ? '2px solid #34d39950' : '1px solid #ffffff08',
                }} />
                {(i === 0 || i === 6) && <span className="text-[7px]" style={{ color: '#c8b89a30' }}>{i === 0 ? '7d ago' : 'today'}</span>}
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-center" style={{ color: '#c8b89a30' }}>Your state evolution over the past 7 days</p>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// SIGNATURE INSIGHT
// ═══════════════════════════════════════════════════════════

function SignatureInsight() {
  // Build from today's data patterns
  return (
    <div data-testid="signature-section">
      <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans'] mb-3" style={{ color: '#c8b89a50' }}>Your Pattern</h2>
      <Card className="bg-white/[0.015] border-white/[0.04]">
        <CardContent className="py-4 px-4 space-y-3">
          <div>
            <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#c8b89a30' }}>Insight</div>
            <p className="text-xs leading-relaxed" style={{ color: '#c8b89a80' }}>
              Your system tends to shift when multiple stressors compound. Recovery is more effective when you maintain consistent routines and address early signals.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#ffffff03' }}>
              <div className="text-[8px] uppercase tracking-wider mb-1" style={{ color: '#fb718540' }}>Blind Spot</div>
              <p className="text-[10px]" style={{ color: '#c8b89a60' }}>Early fatigue signals may go unnoticed until they compound</p>
            </div>
            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#ffffff03' }}>
              <div className="text-[8px] uppercase tracking-wider mb-1" style={{ color: '#34d39940' }}>Stabilizer</div>
              <p className="text-[10px]" style={{ color: '#c8b89a60' }}>Consistent sleep and short reset sessions help maintain balance</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SCORES BARS
// ═══════════════════════════════════════════════════════════

function ScoresBars() {
  const [expanded, setExpanded] = useState(false);
  const [scores, setScores] = useState(null);

  useEffect(() => {
    axios.get(`${API}/twinflo/my-scores`).then(r => setScores(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="scores-section">
      <button className="w-full flex items-center justify-between mb-3" onClick={() => setExpanded(!expanded)}>
        <h2 className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans']" style={{ color: '#c8b89a50' }}>System Balance</h2>
        <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} style={{ color: '#c8b89a30' }} />
      </button>
      {expanded && (
        <Card className="bg-white/[0.015] border-white/[0.04]">
          <CardContent className="py-3 px-4 space-y-2">
            {SCORE_META.map(s => {
              const val = scores?.[s.key] ?? 50;
              const isGood = s.good === 'high' ? val >= 50 : val <= 40;
              const color = isGood ? '#34d399' : val > 60 && s.good === 'low' ? '#f87171' : '#fbbf24';
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px]" style={{ color: '#c8b89a50' }}>{s.label}</span>
                    <span className="text-[10px] font-semibold" style={{ color }}>{val}</span>
                  </div>
                  <div className="h-1 rounded-full" style={{ backgroundColor: '#ffffff06' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
            {scores?.risk_direction && (
              <p className="text-[9px] pt-1" style={{ color: '#c8b89a25' }}>Direction: {scores.risk_direction}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
